import { useCallback, useEffect, useRef } from 'react';

import logger from 'src/clients/LoggingClient';
import { ApiEnv } from 'src/clients/Network/http/fetch';

const RECAPTCHA_API_KEY_DEV = '6LeZ6QIqAAAAAIc45-1gHaRwqGStdVqrRu6EFgdC';
const RECAPTCHA_API_KEY_PROD = '6Leq-w4qAAAAAEAT7gwYSJ21_sYIejmG73oR3fAO';

export const RECAPTCHA_API_KEY = ApiEnv === 'production' ? RECAPTCHA_API_KEY_PROD : RECAPTCHA_API_KEY_DEV;

function assertWindow(): Window {
  if (!window) {
    throw new Error('assertWindow was called outside of a browser');
  }
  return window;
}

/**
 * Used to initialize and cleanup recaptcha script.
 */
export const useRecaptcha = (shouldUseRecaptcha: boolean = true) => {
  const scriptNodeId = useRef<string>(undefined);
  const isRecaptchaInitialized = useRef(false);

  const initializeRecaptcha = useCallback(() => {
    const script = assertWindow().document.createElement('script');
    scriptNodeId.current = 'g-recaptcha-script';
    script.id = scriptNodeId.current;
    script.type = 'text/javascript';
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_API_KEY}&badge=bottomleft`;
    // TODO (szhang): Determine if needed
    // script.nonce = globalConstants.CSP_NONCE
    assertWindow().document.head.appendChild(script);
    const recaptchaStyle = assertWindow().document.createElement('style');
    recaptchaStyle.textContent = '.grecaptcha-badge { visibility: hidden; }';
    assertWindow().document.head.appendChild(recaptchaStyle);
  }, []);

  const cleanupRecaptcha = useCallback(() => {
    if (!scriptNodeId.current) return;
    const script = assertWindow().document.getElementById(scriptNodeId.current);
    if (script) {
      script.remove();
    }
  }, []);

  useEffect(() => {
    if (!isRecaptchaInitialized.current && shouldUseRecaptcha) {
      initializeRecaptcha();
      isRecaptchaInitialized.current = true;
    }
    return cleanupRecaptcha;
  }, [cleanupRecaptcha, initializeRecaptcha, shouldUseRecaptcha]);
};

/**
 * Returns executed recaptcha token to send to backend services
 */
export const executeRecaptcha = async (action: 'signup' | 'login'): Promise<string | null> => {
  return new Promise<string | null>((resolve) => {
    try {
      // @ts-ignore: grecaptcha is injected by the recaptcha script at runtime
      assertWindow().grecaptcha.enterprise.ready(async () => {
        try {
          // @ts-ignore: grecaptcha is injected by the recaptcha script at runtime
          const token = await assertWindow().grecaptcha.enterprise.execute(RECAPTCHA_API_KEY, { action });
          resolve(token);
        } catch (e) {
          logger.error(e, { source: 'executeRecaptcha' });
          resolve(null);
        }
      });
    } catch (e) {
      logger.error(e, { source: 'executeRecaptcha' });
      resolve(null);
    }
  });
};
